import { FC, useEffect, useMemo, useRef, useState } from 'react';
import {
    useTranslate,
    IResourceComponentsProps,
    HttpError,
    CrudFilters,
} from '@refinedev/core';
import { Table, Form } from 'antd';
import type { TablePaginationConfig } from 'antd';
import { TextField, useTable, EditButton } from '@refinedev/antd';
import {
    IApplication,
    IColumn,
    IRecordFilterVariables,
} from 'interfaces/preliminary-application.interface';
import WidthLimiter from '../../../components/width-limiter';
import buildSubmissionRow from '../../../utils/buildSubmissionRow';
import { CreateButton } from '../../../components/ui/create-button';
import { Heading } from '../../../components/ui/heading';
import { useDictionarySelectProps } from '../../../hooks/preliminary-application/useDictionarySelectProps';
import { useProcedureSelectProps } from '../../../hooks/useProcedureSelectProps';
import type { FormInstance } from 'antd';
import styles from './list.module.scss';
import {
    FilterControls,
    type FilterDropdownOption,
} from '../../../components/applications/filter-controls';
import TablePagination from '../../../components/ui/table-pagination';

type ExtendedPaginationConfig = TablePaginationConfig & {
    pageSizeSelectorWidth?: number | string;
};

export const PreliminaryApplicationList: FC<IResourceComponentsProps> = () => {
    const t = useTranslate();
    const {
        tableProps,
        tableQueryResult,
        searchFormProps,
        filters: activeFilters,
        setFilters,
        current,
        setCurrent,
        pageSize,
        setPageSize,
    } = useTable<IApplication, HttpError, IRecordFilterVariables>({
        filters: {
            defaultBehavior: 'replace',
        },
        onSearch: params => {
            const filters: CrudFilters = [];
            const {
                state_id,
                reg_num,
                preliminary_request_procedure_id,
                query,
            } = params;

            filters.push(
                {
                    field: 'query',
                    operator: 'eq',
                    value: query,
                },
                {
                    field: 'reg_num',
                    operator: 'eq',
                    value: reg_num,
                },
                {
                    field: 'state_id',
                    operator: 'eq',
                    value: state_id,
                },
                {
                    field: 'preliminary_request_procedure_id',
                    operator: 'eq',
                    value: preliminary_request_procedure_id,
                }
            );

            return filters;
        },
        resource: 'preliminary-request',
    });

    const { statusProps } = useDictionarySelectProps();
    const { procedureProps, CustomProcedureSelectProps, procedureQueryResult } =
        useProcedureSelectProps({
            filterMode: 'excludePrice',
            optionLabel: 'name_for_applicant',
        });

    const formInstance = searchFormProps.form as FormInstance | undefined;
    const regNumValue = Form.useWatch('reg_num', formInstance);
    const stateValue = Form.useWatch('state_id', formInstance);
    const procedureValue = Form.useWatch(
        'preliminary_request_procedure_id',
        formInstance
    );
    const { pagination: rawPagination, ...restTableProps } = tableProps;
    const pagination =
        rawPagination === false
            ? undefined
            : (rawPagination as ExtendedPaginationConfig);

    const { options: stateOptions, onChange: originalStateOnChange } =
        statusProps ?? {};

    const {
        options: baseProcedureOptions,
        onChange: originalProcedureOnChange,
    } = procedureProps ?? {};

    const [procedureOptions, setProcedureOptions] = useState<
        FilterDropdownOption[] | null
    >((baseProcedureOptions as FilterDropdownOption[] | undefined) ?? null);

    useEffect(() => {
        const fetchData = async () => {
            const items = await CustomProcedureSelectProps();
            setProcedureOptions(items as FilterDropdownOption[]);
        };

        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [procedureQueryResult.data]);

    const resolvedProcedureOptions = useMemo<FilterDropdownOption[]>(() => {
        if (procedureOptions) {
            return procedureOptions;
        }

        return (
            (baseProcedureOptions as FilterDropdownOption[] | undefined) ?? []
        );
    }, [procedureOptions, baseProcedureOptions]);

    const stateSelectOptions = useMemo<FilterDropdownOption[]>(() => {
        if (!stateOptions) return [];

        return stateOptions as FilterDropdownOption[];
    }, [stateOptions]);

    const procedureSelectOptions = useMemo<FilterDropdownOption[]>(() => {
        return resolvedProcedureOptions ?? [];
    }, [resolvedProcedureOptions]);

    const submitAfterChange = (handler?: (value: any, option: any) => void) => {
        return (value: any, option: any) => {
            handler?.(value, option);
            formInstance?.submit();
        };
    };

    const removeFilterByField = (field: string) => {
        setFilters(prevFilters =>
            prevFilters.filter(filter => {
                if (!('field' in filter)) return true;

                return filter.field !== field;
            })
        );
    };

    const handleStateSelect = (
        nextValue: string | number | undefined,
        option?: FilterDropdownOption
    ) => {
        if (!formInstance) return;

        const resolvedValue = nextValue ?? (option ? option.value : undefined);

        const shouldClear =
            resolvedValue === undefined ||
            resolvedValue === null ||
            (typeof resolvedValue === 'string' && resolvedValue.trim() === '');

        if (shouldClear) {
            formInstance.setFieldsValue({ state_id: undefined });
            originalStateOnChange?.(undefined as any, undefined as any);
            removeFilterByField('state_id');
            formInstance.submit();
            return;
        }

        const matchedOption =
            option ??
            stateSelectOptions.find(
                item => String(item.value) === String(resolvedValue)
            );

        formInstance.setFieldsValue({ state_id: resolvedValue });
        submitAfterChange(originalStateOnChange)(resolvedValue, matchedOption);
    };

    const handleProcedureSelect = (
        nextValue: string | number | undefined,
        option?: FilterDropdownOption
    ) => {
        if (!formInstance) return;

        const resolvedValue = nextValue ?? (option ? option.value : undefined);

        const shouldClear =
            resolvedValue === undefined ||
            resolvedValue === null ||
            (typeof resolvedValue === 'string' && resolvedValue.trim() === '');

        if (shouldClear) {
            formInstance.setFieldsValue({
                preliminary_request_procedure_id: undefined,
            });
            originalProcedureOnChange?.(undefined as any, undefined as any);
            removeFilterByField('preliminary_request_procedure_id');
            formInstance.submit();
            return;
        }

        const matchedOption =
            option ??
            procedureSelectOptions.find(
                item => String(item.value) === String(resolvedValue)
            );

        formInstance.setFieldsValue({
            preliminary_request_procedure_id: resolvedValue,
        });
        submitAfterChange(originalProcedureOnChange)(
            resolvedValue,
            matchedOption
        );
    };

    const clearedInitialFilters = useRef(false);
    const isInitialRegNumWatch = useRef(true);

    const clearLabelText = t('applications.filters.clear');

    useEffect(() => {
        if (!formInstance || clearedInitialFilters.current) return;

        const hasInitialFilter = activeFilters.some(filter => {
            if (!('field' in filter)) return false;

            return (
                filter.field === 'state_id' ||
                filter.field === 'preliminary_request_procedure_id' ||
                filter.field === 'reg_num'
            );
        });

        if (hasInitialFilter) {
            setFilters(prevFilters =>
                prevFilters.filter(filter => {
                    if (!('field' in filter)) return true;

                    return (
                        filter.field !== 'state_id' &&
                        filter.field !== 'preliminary_request_procedure_id' &&
                        filter.field !== 'reg_num'
                    );
                })
            );
        }

        formInstance.setFieldsValue({
            state_id: undefined,
            preliminary_request_procedure_id: undefined,
            reg_num: undefined,
        });

        clearedInitialFilters.current = true;
    }, [activeFilters, formInstance, setFilters]);

    const isAllActive =
        !formInstance || (!stateValue && !procedureValue && !regNumValue);

    const handleResetFilters = () => {
        if (!formInstance) return;

        formInstance.setFieldsValue({
            state_id: undefined,
            preliminary_request_procedure_id: undefined,
            reg_num: undefined,
        });

        originalStateOnChange?.(undefined as any, undefined as any);
        originalProcedureOnChange?.(undefined as any, undefined as any);

        setFilters(prevFilters =>
            prevFilters.filter(filter => {
                if (!('field' in filter)) return true;

                return (
                    filter.field !== 'state_id' &&
                    filter.field !== 'preliminary_request_procedure_id' &&
                    filter.field !== 'reg_num'
                );
            })
        );
        formInstance.submit();
    };

    useEffect(() => {
        if (!formInstance) return;

        if (isInitialRegNumWatch.current) {
            isInitialRegNumWatch.current = false;
            return;
        }

        const handler = window.setTimeout(() => {
            formInstance.submit();
        }, 300);

        return () => window.clearTimeout(handler);
    }, [formInstance, regNumValue]);

    const currentPage = current ?? 1;
    const resolvedPageSize = pageSize ?? pagination?.pageSize ?? 10;
    const totalItems = tableQueryResult?.data?.total ?? pagination?.total ?? 0;
    const handlePageChange = (page: number) => {
        setCurrent?.(page);
    };

    const defaultPageSizeOptions = ['10', '25', '50', '100'];
    const rawPageSizeOptions = (
        pagination?.pageSizeOptions ?? defaultPageSizeOptions
    ).map(option => option.toString());

    const uniquePageSizeOptions = rawPageSizeOptions.reduce<string[]>(
        (acc, current) => {
            if (!acc.includes(current)) acc.push(current);
            return acc;
        },
        []
    );

    const orderedPageSizeOptions = defaultPageSizeOptions.filter(option =>
        uniquePageSizeOptions.includes(option)
    );

    uniquePageSizeOptions.forEach(option => {
        if (!orderedPageSizeOptions.includes(option)) {
            orderedPageSizeOptions.push(option);
        }
    });

    if (!orderedPageSizeOptions.includes(resolvedPageSize.toString())) {
        orderedPageSizeOptions.push(resolvedPageSize.toString());
    }

    const handlePageSizeChange = (value: number) => {
        if (Number.isNaN(value)) return;

        setPageSize?.(value);
        setCurrent?.(1);
    };

    const pageSizeSelectorWidth = pagination?.pageSizeSelectorWidth;
    const resolvedPageSizeSelectorWidth =
        typeof pageSizeSelectorWidth === 'number'
            ? `${pageSizeSelectorWidth}px`
            : pageSizeSelectorWidth;

    return (
        <div className={styles.pageWrapper}>
            <header className={styles.header}>
                <Heading level={1} align="center" className={styles.title}>
                    {t('preliminary-applications.preliminary-applications')}
                </Heading>
                <FilterControls
                    formProps={searchFormProps}
                    hiddenFieldNames={[
                        'state_id',
                        'preliminary_request_procedure_id',
                    ]}
                    searchFieldName="reg_num"
                    searchPlaceholder={t('search.placeholder')}
                    filtersLabel={t('applications.titles.filters')}
                    clearFilterLabel={clearLabelText}
                    isAllActive={isAllActive}
                    onResetFilters={handleResetFilters}
                    dropdowns={[
                        {
                            placeholder: t(
                                'preliminary-applications.fields.state'
                            ),
                            value: stateValue,
                            options: stateSelectOptions,
                            onSelect: handleStateSelect,
                            clearLabel: clearLabelText,
                            showSearch: true,
                            onSearch: value => statusProps?.onSearch?.(value),
                            loading: statusProps?.loading,
                            searchPlaceholder: t(
                                'preliminary-applications.fields.state'
                            ),
                        },
                        {
                            placeholder: t(
                                'preliminary-applications.fields.procedure'
                            ),
                            value: procedureValue,
                            options: procedureSelectOptions,
                            onSelect: handleProcedureSelect,
                            clearLabel: clearLabelText,
                            showSearch: true,
                            onSearch: value =>
                                procedureProps?.onSearch?.(value),
                            loading: procedureProps?.loading,
                            searchPlaceholder: t(
                                'preliminary-applications.fields.procedure'
                            ),
                        },
                    ]}
                    extraActions={
                        <div className={styles.createButtonWrapper}>
                            <CreateButton
                                resource="preliminary-applications"
                                label={t(
                                    'preliminary-applications.buttons.create_an_application_with_ev'
                                )}
                                className={styles.createButton}
                                variant="primary"
                            />
                        </div>
                    }
                />
            </header>
            <div className={styles.tableWrapper}>
                <Table
                    {...restTableProps}
                    pagination={false}
                    rowKey="id"
                    className={styles.table}
                    tableLayout="fixed"
                >
                    <Table.Column
                        dataIndex="medication_name"
                        key="medication_name"
                        title={t('applications.fields.medication_name')}
                        render={(value, row: IColumn) => (
                            <div className={styles.cellContent}>
                                <WidthLimiter>
                                    <TextField value={value} />
                                </WidthLimiter>
                            </div>
                        )}
                        sorter
                        width={300}
                        align="left"
                    />
                    <Table.Column
                        dataIndex="reg_num"
                        key="reg_num"
                        title={t('applications.fields.moz_data')}
                        render={(value, row: IColumn) => {
                            const dateSuffix = t(
                                'applications.wizard.application_date_suffix'
                            );
                            const val = value
                                ? buildSubmissionRow(
                                      row,
                                      ['reg_num', 'reg_date'],
                                      ` ${dateSuffix} `
                                  )
                                : '';

                            return val && <TextField value={val} />;
                        }}
                        sorter
                        width={300}
                        align="left"
                    />
                    <Table.Column
                        dataIndex="state_id"
                        key="state_id"
                        title={t('applications.fields.state')}
                        render={(_, row: any) => (
                            <TextField value={row?.state?.name} />
                        )}
                        sorter
                        width={280}
                        align="left"
                    />
                    <Table.Column
                        title={t('table.actions')}
                        dataIndex="actions"
                        render={(_, record: any) => {
                            const itemId = record.id;

                            return (
                                <EditButton
                                    hideText
                                    size="small"
                                    recordItemId={itemId}
                                />
                            );
                        }}
                        align="center"
                        width={120}
                    />
                </Table>
            </div>
            {totalItems ? (
                <TablePagination
                    currentPage={currentPage}
                    pageSize={resolvedPageSize}
                    totalItems={totalItems}
                    pageSizeOptions={orderedPageSizeOptions}
                    onChangePage={handlePageChange}
                    onChangePageSize={handlePageSizeChange}
                    pageSizeSelectorWidth={resolvedPageSizeSelectorWidth}
                    previousLabel={t('applications.pagination.previous')}
                    nextLabel={t('applications.pagination.next')}
                    showDocumentsLabel={t(
                        'applications.pagination.show_documents'
                    )}
                    rangeLabel={(start, end, total) =>
                        `${start}-${end} ${t(
                            'applications.pagination.of_total'
                        )} ${total}`
                    }
                />
            ) : null}
        </div>
    );
};
